import React, { useState, useEffect } from 'react';
import { Save, Upload, Image as ImageIcon, X } from 'lucide-react';
import ApiService from '../../services/api';

interface HomeSettings {
  id: number;
  heroBackgroundImage?: string;
  artistPortraitImage?: string;
  showcaseImage1?: string;
  showcaseImage2?: string;
  showcaseImage3?: string;
  contactBackgroundImage?: string;
  galleryHeroImage?: string;
  heroTitle: string;
  heroSubtitle?: string;
  heroDescription?: string;
  artistTitle: string;
  artistDescription?: string;
  showcaseDescription?: string;
  testimonialsTitle: string;
  testimonialsDescription?: string;
  contactTitle: string;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  isActive: boolean;
}

const HomeSettingsManagement: React.FC = () => {
  const [settings, setSettings] = useState<HomeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    heroTitle: '',
    heroSubtitle: '',
    heroDescription: '',
    artistTitle: '',
    artistDescription: '',
    showcaseDescription: '',
    testimonialsTitle: '',
    testimonialsDescription: '',
    contactTitle: '',
    backgroundColor: '#000000',
    secondaryBackgroundColor: '#1f2937'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAdminHomeSettings();
      setSettings(response.settings);
      setFormData({
        heroTitle: response.settings.heroTitle || '',
        heroSubtitle: response.settings.heroSubtitle || '',
        heroDescription: response.settings.heroDescription || '',
        artistTitle: response.settings.artistTitle || '',
        artistDescription: response.settings.artistDescription || '',
        showcaseDescription: response.settings.showcaseDescription || '',
        testimonialsTitle: response.settings.testimonialsTitle || '',
        testimonialsDescription: response.settings.testimonialsDescription || '',
        contactTitle: response.settings.contactTitle || '',
        backgroundColor: response.settings.backgroundColor || '#000000',
        secondaryBackgroundColor: response.settings.secondaryBackgroundColor || '#1f2937'
      });
    } catch (error: any) {
      console.error('Settings load error:', error);
      setError('Ayarlar yüklenirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await ApiService.updateHomeSettings(formData);
      setSuccess('Ayarlar başarıyla güncellendi!');
      await loadSettings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Save error:', error);
      setError('Ayarlar kaydedilirken hata oluştu: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (field: string, file: File | null) => {
    try {
      setLoading(true);
      
      if (file) {
        const response = await ApiService.uploadHomeImage(file);
        
        // Update the specific image field
        const updateData = { [field]: response.imagePath };
        await ApiService.updateHomeSettings(updateData);
        
        setSuccess(`${field} resmi başarıyla yüklendi!`);
      } else {
        // Remove the image
        const updateData = { [field]: null };
        await ApiService.updateHomeSettings(updateData);
        
        setSuccess(`${field} resmi başarıyla silindi!`);
      }
      
      await loadSettings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Image upload error:', error);
      setError('Resim işlemi sırasında hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath: string | undefined) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('/uploads/')) {
      return `http://localhost:5000${imagePath}`;
    }
    return imagePath;
  };

  if (loading && !settings) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-700 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="h-16 bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-light text-white">Ana Sayfa Ayarları</h1>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-900 border border-green-700 text-green-200 rounded">
          <span className="block sm:inline">{success}</span>
          <button 
            onClick={() => setSuccess(null)}
            className="float-right text-green-200 hover:text-green-100"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-900 border border-red-700 text-red-200 rounded">
          <span className="block sm:inline">{error}</span>
          <button 
            onClick={() => setError(null)}
            className="float-right text-red-200 hover:text-red-100"
          >
            ×
          </button>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-8">
        {/* Hero Section */}
        <div className="p-6 rounded-lg border" style={{ backgroundColor: settings?.secondaryBackgroundColor || '#1f2937', borderColor: settings?.secondaryBackgroundColor || '#374151' }}>
          <h2 className="text-xl font-light text-white mb-6">Hero Bölümü</h2>
          
          {/* Hero Background Image */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Hero Arka Plan Resmi
            </label>
            <div className="flex items-center space-x-4">
              {settings?.heroBackgroundImage && (
                <div className="relative">
                  <img 
                    src={getImageUrl(settings.heroBackgroundImage)} 
                    alt="Hero Background" 
                    className="w-32 h-20 object-cover rounded border border-gray-600"
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('heroBackgroundImage', null)}
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload('heroBackgroundImage', file);
                  }}
                  className="hidden"
                  id="heroBackgroundImage"
                />
                <label
                  htmlFor="heroBackgroundImage"
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Resim Yükle
                </label>
                <p className="text-xs text-gray-400 mt-1">
                  Önerilen boyut: 1920x1080px (16:9 oranı)
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Hero Başlığı
              </label>
              <input
                type="text"
                value={formData.heroTitle}
                onChange={(e) => setFormData({ ...formData, heroTitle: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                style={{ backgroundColor: settings?.backgroundColor || '#000000' }}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Hero Alt Başlığı
              </label>
              <input
                type="text"
                value={formData.heroSubtitle}
                onChange={(e) => setFormData({ ...formData, heroSubtitle: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                style={{ backgroundColor: settings?.backgroundColor || '#000000' }}
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Hero Açıklaması
            </label>
            <textarea
              value={formData.heroDescription}
              onChange={(e) => setFormData({ ...formData, heroDescription: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
              style={{ backgroundColor: settings?.backgroundColor || '#000000' }}
            />
          </div>
        </div>

        {/* Artist Section */}
        <div className="p-6 rounded-lg border" style={{ backgroundColor: settings?.secondaryBackgroundColor || '#1f2937', borderColor: settings?.secondaryBackgroundColor || '#374151' }}>
          <h2 className="text-xl font-light text-white mb-6">Sanatçı Bölümü</h2>
          
          {/* Artist Portrait Image */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sanatçı Portre Resmi
            </label>
            <div className="flex items-center space-x-4">
              {settings?.artistPortraitImage && (
                <div className="relative">
                  <img 
                    src={getImageUrl(settings.artistPortraitImage)} 
                    alt="Artist Portrait" 
                    className="w-32 h-40 object-cover rounded border border-gray-600"
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('artistPortraitImage', null)}
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload('artistPortraitImage', file);
                  }}
                  className="hidden"
                  id="artistPortraitImage"
                />
                <label
                  htmlFor="artistPortraitImage"
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Resim Yükle
                </label>
                <p className="text-xs text-gray-400 mt-1">
                  Önerilen boyut: 800x1000px (4:5 oranı)
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Sanatçı Başlığı
              </label>
              <input
                type="text"
                value={formData.artistTitle}
                onChange={(e) => setFormData({ ...formData, artistTitle: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                style={{ backgroundColor: settings?.backgroundColor || '#000000' }}
                required
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sanatçı Açıklaması
            </label>
            <textarea
              value={formData.artistDescription}
              onChange={(e) => setFormData({ ...formData, artistDescription: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
            />
          </div>
        </div>

        {/* Showcase Section */}
        <div className="p-6 rounded-lg border" style={{ backgroundColor: settings?.secondaryBackgroundColor || '#1f2937', borderColor: settings?.secondaryBackgroundColor || '#374151' }}>
          <h2 className="text-xl font-light text-white mb-6">Showcase Bölümü</h2>
          
          {/* Showcase Images */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Image 1 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Showcase Resim 1 (Sol)
              </label>
              <div className="space-y-2">
                                 {settings?.showcaseImage1 && (
                   <div className="relative">
                     <img 
                       src={getImageUrl(settings.showcaseImage1)} 
                       alt="Showcase 1" 
                       className="w-full h-32 object-cover rounded border border-gray-600"
                     />
                     <button
                       type="button"
                       onClick={() => handleImageUpload('showcaseImage1', null)}
                       className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-700 text-xs"
                     >
                       <X className="w-3 h-3" />
                     </button>
                   </div>
                 )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload('showcaseImage1', file);
                  }}
                  className="w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                />
                <p className="text-xs text-gray-400">
                  Önerilen boyut: 400x400px (1:1 oranı). GIF, JPG, PNG desteklenir. Boyut sınırı yoktur.
                </p>
              </div>
            </div>

            {/* Image 2 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Showcase Resim 2 (Orta)
              </label>
              <div className="space-y-2">
                                 {settings?.showcaseImage2 && (
                   <div className="relative">
                     <img 
                       src={getImageUrl(settings.showcaseImage2)} 
                       alt="Showcase 2" 
                       className="w-full h-32 object-cover rounded border border-gray-600"
                     />
                     <button
                       type="button"
                       onClick={() => handleImageUpload('showcaseImage2', null)}
                       className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-700 text-xs"
                     >
                       <X className="w-3 h-3" />
                     </button>
                   </div>
                 )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload('showcaseImage2', file);
                  }}
                  className="w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                />
                <p className="text-xs text-gray-400">
                  Önerilen boyut: 400x500px (4:5 oranı). GIF, JPG, PNG desteklenir. Boyut sınırı yoktur.
                </p>
              </div>
            </div>

            {/* Image 3 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Showcase Resim 3 (Sağ)
              </label>
              <div className="space-y-2">
                                 {settings?.showcaseImage3 && (
                   <div className="relative">
                     <img 
                       src={getImageUrl(settings.showcaseImage3)} 
                       alt="Showcase 3" 
                       className="w-full h-32 object-cover rounded border border-gray-600"
                     />
                     <button
                       type="button"
                       onClick={() => handleImageUpload('showcaseImage3', null)}
                       className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-700 text-xs"
                     >
                       <X className="w-3 h-3" />
                     </button>
                   </div>
                 )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload('showcaseImage3', file);
                  }}
                  className="w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                />
                <p className="text-xs text-gray-400">
                  Önerilen boyut: 400x400px (1:1 oranı). GIF, JPG, PNG desteklenir. Boyut sınırı yoktur.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Showcase Açıklaması
            </label>
            <textarea
              value={formData.showcaseDescription}
              onChange={(e) => setFormData({ ...formData, showcaseDescription: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
            />
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="p-6 rounded-lg border" style={{ backgroundColor: settings?.secondaryBackgroundColor || '#1f2937', borderColor: settings?.secondaryBackgroundColor || '#374151' }}>
          <h2 className="text-xl font-light text-white mb-6">Müşteri Yorumları Bölümü</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Müşteri Yorumları Başlığı
              </label>
              <input
                type="text"
                value={formData.testimonialsTitle}
                onChange={(e) => setFormData({ ...formData, testimonialsTitle: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                required
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Müşteri Yorumları Açıklaması
            </label>
            <textarea
              value={formData.testimonialsDescription}
              onChange={(e) => setFormData({ ...formData, testimonialsDescription: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
            />
          </div>
        </div>

        {/* Color Settings Section */}
        <div className="p-6 rounded-lg border" style={{ backgroundColor: settings?.secondaryBackgroundColor || '#1f2937', borderColor: settings?.secondaryBackgroundColor || '#374151' }}>
          <h2 className="text-xl font-light text-white mb-6">Renk Ayarları</h2>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Ana Arka Plan Rengi
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="color"
                value={formData.backgroundColor}
                onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
                className="w-16 h-12 border border-gray-600 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.backgroundColor}
                onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
                className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500 font-mono text-sm"
                placeholder="#000000"
              />
              <div className="text-sm text-gray-400">
                Ana arkaplan rengini değiştirin
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Bu renk tüm ana arkaplan alanlarını etkileyecektir
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              İkincil Arka Plan Rengi
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="color"
                value={formData.secondaryBackgroundColor}
                onChange={(e) => setFormData({ ...formData, secondaryBackgroundColor: e.target.value })}
                className="w-16 h-12 border border-gray-600 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.secondaryBackgroundColor}
                onChange={(e) => setFormData({ ...formData, secondaryBackgroundColor: e.target.value })}
                className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500 font-mono text-sm"
                placeholder="#1f2937"
              />
              <div className="text-sm text-gray-400">
                CTA bölümleri arkaplan rengini değiştirin
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Bu renk CTA bölümleri ve diğer ikincil arkaplan alanlarını etkileyecektir
            </p>
          </div>
        </div>

        {/* Gallery Section */}
        <div className="p-6 rounded-lg border" style={{ backgroundColor: settings?.secondaryBackgroundColor || '#1f2937', borderColor: settings?.secondaryBackgroundColor || '#374151' }}>
          <h2 className="text-xl font-light text-white mb-6">Galeri Bölümü</h2>
          
          {/* Gallery Hero Image */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Galeri Hero Arka Plan Resmi
            </label>
            <div className="flex items-center space-x-4">
              {settings?.galleryHeroImage && (
                <div className="relative">
                  <img 
                    src={getImageUrl(settings.galleryHeroImage)} 
                    alt="Gallery Hero Background" 
                    className="w-32 h-20 object-cover rounded border border-gray-600"
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('galleryHeroImage', null)}
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageUpload('galleryHeroImage', file);
                }}
                className="flex-1 text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
              />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Galeri sayfası hero bölümü için arka plan resmi. Önerilen boyut: 1920x1080px. GIF, JPG, PNG desteklenir.
            </p>
          </div>
        </div>

        {/* Contact Section */}
        <div className="p-6 rounded-lg border" style={{ backgroundColor: settings?.secondaryBackgroundColor || '#1f2937', borderColor: settings?.secondaryBackgroundColor || '#374151' }}>
          <h2 className="text-xl font-light text-white mb-6">İletişim Bölümü</h2>
          
          {/* Contact Background Image */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              İletişim Arka Plan Resmi
            </label>
            <div className="flex items-center space-x-4">
              {settings?.contactBackgroundImage && (
                <div className="relative">
                  <img 
                    src={getImageUrl(settings.contactBackgroundImage)} 
                    alt="Contact Background" 
                    className="w-32 h-20 object-cover rounded border border-gray-600"
                  />
                  <button
                    type="button"
                    onClick={() => handleImageUpload('contactBackgroundImage', null)}
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload('contactBackgroundImage', file);
                  }}
                  className="hidden"
                  id="contactBackgroundImage"
                />
                <label
                  htmlFor="contactBackgroundImage"
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Resim Yükle
                </label>
                <p className="text-xs text-gray-400 mt-1">
                  Önerilen boyut: 1920x1080px (16:9 oranı)
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              İletişim Başlığı
            </label>
            <input
              type="text"
              value={formData.contactTitle}
              onChange={(e) => setFormData({ ...formData, contactTitle: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
              required
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center px-6 py-3 bg-pink-600 text-white rounded hover:bg-pink-700 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HomeSettingsManagement;
